// Reading and checking receipts/v1 (see /receipts/v1 and
// app/models/card/receipt_payload.rb, which builds the unsigned part).
//
//   OP_FALSE OP_RETURN <protocol> "sha256" <hex> <url> <media type>
//     | <AIP prefix> "BITCOIN_ECDSA" <address> <compact signature>
//
// AIP decoding and signature checks are @1sat/templates, the same code
// Yours Wallet uses, so a receipt Yours signs is checked the way Yours checks.
import { OP, Script, Utils } from '@bsv/sdk';
import { AIP, BitCom } from '@1sat/templates';

export const PROTOCOL = 'https://ryanwold.net/receipts/v1';
export const BAP_API = 'https://api.1sat.app/1sat/bap';
export const WOC_TX = 'https://api.whatsonchain.com/v1/bsv/main/tx/hash/';

export const sha256Hex = async (bytes) => {
  if (!globalThis.crypto?.subtle) throw new Error('no-subtle');
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

// The unsigned receipt script, one push per field, ready for AIP signing.
// Built push by push on purpose: Script.fromHex folds everything after
// OP_RETURN into a single chunk, and AIP would then sign bytes that no
// verifier reconstructs. Must equal Card::ReceiptPayload#script_hex.
export function buildUnsignedReceipt(fields) {
  const script = new Script();
  script.writeOpCode(OP.OP_FALSE);
  script.writeOpCode(OP.OP_RETURN);
  for (const field of fields) script.writeBin(Utils.toArray(field, 'utf8'));
  return script;
}

// → { digest, url, mediaType, signer: { address, valid } | null }, or null if
// the script is not a receipts/v1 output.
export function readReceipt(scriptHex) {
  let bitcom;
  try {
    bitcom = BitCom.decode(Script.fromHex(scriptHex));
  } catch {
    return null;
  }
  const head = bitcom?.protocols?.[0];
  if (!head || head.protocol !== PROTOCOL) return null;

  const [algorithm, digest, url, mediaType] = Script.fromBinary(head.script).chunks.map((c) =>
    Utils.toUTF8(c.data ?? []),
  );
  if (algorithm !== 'sha256') return null;

  const aip = AIP.decode(bitcom)[0];
  return {
    digest,
    url,
    mediaType,
    signer: aip ? { address: aip.data.address, valid: aip.data.valid === true } : null,
  };
}

// The receipt output in a WhatsOnChain tx for this digest, if any.
export function findReceipt(tx, digest) {
  for (const out of tx?.vout ?? []) {
    const receipt = readReceipt(out.scriptPubKey?.hex ?? '');
    if (receipt?.digest === digest) return receipt;
  }
  return null;
}

// Was `address` a signing key of BAP identity `bapId` at `block`? Handles key
// rotation: the indexer answers for that height, not just for today.
// → { ok: true } | { ok: false, reason }
export async function checkBapSigner({ address, block, bapId, fetchImpl = fetch }) {
  const res = await fetchImpl(`${BAP_API}/identity/validByAddress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address, block: block || 0 }),
  });
  if (!res.ok) return { ok: false, reason: `BAP indexer returned ${res.status}` };
  const body = await res.json();
  if (body?.identity?.idKey !== bapId) return { ok: false, reason: 'signer belongs to a different identity' };
  if (!body?.validityRecord?.valid) return { ok: false, reason: 'signer key was not valid for the identity at that block' };
  return { ok: true };
}
